// src/context/UserContext.js

import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import {
    collection, doc, getDocs,
    updateDoc, deleteDoc, serverTimestamp, setDoc
} from 'firebase/firestore';

import { useAuth } from './AuthContext';
import { getPaths } from '../firebase.paths';
import { globalAppId, globalFirebaseConfig } from '../utils/constants';

const UserContext = createContext();

export const useUserData = () => {
    const context = useContext(UserContext);
    if (!context) throw new Error('useUserData deve ser usado dentro de um UserDataProvider');
    return context;
};

export { UserContext };

export const UserDataProvider = ({ children }) => {
    const { db } = useAuth();
    const paths = useMemo(() => getPaths(globalAppId), []);

    const [leaderboard, setLeaderboard] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(true);

    // Busca pontual do leaderboard — sem listener em tempo real para economizar leituras.
    // Chamada no boot e após cada mutação (handleUpdate*, handleToggle*, handleDelete*, handleRegister*).
    const refreshLeaderboard = useCallback(async () => {
        if (!db) return;
        setIsDataLoading(true);
        try {
            const snap = await getDocs(collection(db, paths.users));
            console.log('[UserContext] leaderboard docs retornados:', snap.size);
            const list = snap.docs
                .map(d => ({
                    id: d.id,
                    ...d.data(),
                    points: d.data().points || 0,
                    credits: d.data().credits || 0,
                    isActive: d.data().isActive !== false,
                    onVacation: d.data().onVacation || false
                }))
                .filter(u => u.isActive !== false);
            setLeaderboard(list);
        } catch (err) {
            console.error('[UserContext] Erro ao buscar leaderboard:', err.code, err.message, err);
        } finally {
            setIsDataLoading(false);
        }
    }, [db, paths]);

    useEffect(() => {
        if (db) refreshLeaderboard();
    }, [db]); // eslint-disable-line react-hooks/exhaustive-deps -- refreshLeaderboard é estável, db é a dep real

    const handleUpdateUserProfile = useCallback(async (uid, data) => {
        await updateDoc(doc(db, paths.users, uid), data);
        await refreshLeaderboard();
    }, [db, paths, refreshLeaderboard]);

    const handleToggleUserStatus = useCallback(async (uid, currentStatus) => {
        const newStatus = !currentStatus;
        if (newStatus === false) {
            await updateDoc(doc(db, paths.users, uid), {
                isActive: false,
                points: 0,
                credits: 0,
                inactivatedAt: serverTimestamp()
            });
        } else {
            await updateDoc(doc(db, paths.users, uid), { isActive: true });
        }
        await refreshLeaderboard();
    }, [db, paths, refreshLeaderboard]);

    const handleToggleVacation = useCallback(async (uid, currentStatus) => {
        await updateDoc(doc(db, paths.users, uid), { onVacation: !currentStatus });
        await refreshLeaderboard();
    }, [db, paths, refreshLeaderboard]);

    const handleDeleteUser = useCallback(async (uid) => {
        await deleteDoc(doc(db, paths.users, uid));
        await refreshLeaderboard();
    }, [db, paths, refreshLeaderboard]);

    const handleRegisterUser = useCallback(async (name, email, password, role, sector) => {
        const { initializeApp, getApps, getApp } = await import('firebase/app');
        const tempApp = getApps().find(a => a.name === 'tempApp')
            ? getApp('tempApp')
            : initializeApp(globalFirebaseConfig, 'tempApp');

        const { getAuth, createUserWithEmailAndPassword, signOut: tempSignOut } = await import('firebase/auth');
        const tempAuth = getAuth(tempApp);

        try {
            const userCredential = await createUserWithEmailAndPassword(tempAuth, email, password);
            const newUid = userCredential.user.uid;

            await setDoc(doc(db, paths.users, newUid), {
                name, email, role, sector,
                points: 0, credits: 0,
                isActive: true,
                onVacation: false,
                createdAt: serverTimestamp()
            });

            await tempSignOut(tempAuth);
            await refreshLeaderboard();
        } catch (error) {
            throw error;
        }
    }, [db, paths, refreshLeaderboard]);

    const value = useMemo(() => ({
        isDataLoading, leaderboard,
        refreshLeaderboard,
        handleUpdateUserProfile, handleDeleteUser, handleRegisterUser,
        handleToggleUserStatus, handleToggleVacation
    }), [
        isDataLoading, leaderboard,
        refreshLeaderboard,
        handleUpdateUserProfile, handleDeleteUser, handleRegisterUser,
        handleToggleUserStatus, handleToggleVacation
    ]);

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
